import { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Stack, TablePagination, Typography } from '@mui/material';

import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { DashboardContent } from 'src/layouts/dashboard';
import LoadingPanel from 'src/pages/admin/components/LoadingPanel';
import ErrorPage403 from 'src/pages/error/403';
import { paths } from 'src/routes/paths';
import { PermissionGuard } from 'src/routes/components/permission-guard';

import { formatCount, formatInstant, formatNav, formatUnits } from '../vaults/helpers';
import { useVaults } from '../vaults/useVaults';
import { VaultSwitcher } from '../vaults/VaultSwitcher';
import {
  DEFAULT_ROWS_PER_PAGE,
  INVESTORS_NS as NS,
  INVESTORS_PERMISSION_RESOURCE as RESOURCE,
  ROWS_PER_PAGE_OPTIONS,
} from './constants';
import { investorsPath, resolveVault } from './helpers';
import { HoldersTable } from './HoldersTable';
import { useVaultHolders } from './useVaultHolders';

/**
 * §14.5 Investors (XBIT-4278): positions by vault at `/vault/investors/:vaultId`. The vault lives in the URL (§14.1):
 * the switcher navigates, `/vault/investors` opens the first vault, and an id the console does not list falls back
 * the same way. The page is served by the BFF one page at a time, the manager first.
 */
const InvestorsPage = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const root = paths.dashboard.vaultInvestors.root;

  const {
    vaults,
    loading: vaultsLoading,
    loadError: vaultsError,
    refetch: refetchVaults,
  } = useVaults();
  const target = resolveVault(vaults, id);
  const vaultId = vaultsLoading ? null : target.vaultId;

  // The page index belongs to the vault it was chosen on: another vault starts on its first page in the same
  // render, so the old vault's offset is never sent for the new one.
  const [pageAt, setPageAt] = useState<{ vaultId: string | null; index: number }>({
    vaultId: null,
    index: 0,
  });
  const pageIndex = pageAt.vaultId === vaultId ? pageAt.index : 0;
  const setPageIndex = (index: number) => setPageAt({ vaultId, index });
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const holders = useVaultHolders(vaultId, pageIndex * rowsPerPage, rowsPerPage);

  useEffect(() => {
    if (vaultsLoading || vaultsError || !target.redirect) return;
    if (target.unknown) notify(translate(`${NS}.unknownVault`, { id }), { type: 'warning' });
    navigate(investorsPath(root, target.redirect), { replace: true });
    // translate / notify are not identity-stable; the redirect depends on the URL and the list only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaultsLoading, vaultsError, target.redirect, target.unknown, id, root]);

  const page = holders.page && holders.page.vaultId === vaultId ? holders.page : null;
  const refresh = () => (vaultId ? holders.refetch() : refetchVaults());

  return (
    <PermissionGuard resource={RESOURCE} action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate(`${NS}.title`)}
          links={[
            { name: translate('resource.vault'), href: '#' },
            { name: translate(`${NS}.title`) },
          ]}
          action={
            <Button
              variant="outlined"
              startIcon={<Iconify icon="solar:refresh-bold" />}
              onClick={refresh}
              disabled={vaultsLoading || holders.loading}
            >
              {translate(`${NS}.refresh`)}
            </Button>
          }
        />

        <Stack spacing={3} sx={{ mt: 3 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ alignItems: { sm: 'center' } }}
          >
            <VaultSwitcher
              vaults={vaults}
              value={vaultId ?? ''}
              onChange={(next) => navigate(investorsPath(root, next))}
              disabled={vaultsLoading}
            />
            {page && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {translate(`${NS}.summary`, {
                  holders: formatCount(page.total),
                  units: formatUnits(page.totalShares),
                })}
              </Typography>
            )}
          </Stack>

          {vaultsError && <Alert severity="error">{vaultsError}</Alert>}
          {!vaultsLoading && !vaultsError && vaults.length === 0 && (
            <Alert severity="info">{translate(`${NS}.noVaults`)}</Alert>
          )}
          {holders.loadError && <Alert severity="error">{holders.loadError}</Alert>}
          {page &&
            (page.valuedAt && page.nav ? (
              <Alert severity="info">
                {translate(`${NS}.pricedAt`, {
                  nav: formatNav(page.nav),
                  at: formatInstant(page.valuedAt),
                })}
              </Alert>
            ) : (
              <Alert severity="info">{translate(`${NS}.noCutoff`)}</Alert>
            ))}

          {vaultId && (
            <LoadingPanel isLoading={holders.loading && !page}>
              <Stack spacing={1}>
                <HoldersTable holders={page?.items ?? []} />
                {page && page.total > 0 && (
                  <Card>
                    <TablePagination
                      component="div"
                      count={page.total}
                      page={pageIndex}
                      onPageChange={(_, next) => setPageIndex(next)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPageIndex(0);
                      }}
                      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                      labelRowsPerPage={translate(`${NS}.rowsPerPage`)}
                    />
                  </Card>
                )}
              </Stack>
            </LoadingPanel>
          )}
        </Stack>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default InvestorsPage;
